import { ref } from 'vue'

import { clientSession } from '@/services/api'
import { uuid } from '@/services/uuid'
import { dialog } from '@/components/modal/modal'
import { storedUserValues } from './components/information'
import { useToast } from '@/components/toast'

import type { LoadingModalObject } from '@/container/components/loading/Loader.vue'

import type { ExportWorker, JobStatus } from '=/types'

export const { component: LoadingModal, open: openLoadingModal, close: closeLoadingModal } = dialog(() => import('@/container/components/loading/Modal.vue'))

const { showToast } = useToast()

export const loadingStatus = ref<LoadingModalObject>({
  status: '',
  index: 0
})

type JobStatusHandler = Record<string, (status: JobStatus, checkStatus: () => void) => void>
type DownloadErrorPayload = {
  error?: {
    code?: string
    message?: string
  }
}

const pingTimeout = 2500

const downloadErrorMessages = {
  nl: {
    default: 'Er is een fout opgetreden bij het downloaden van de bestanden. Probeer het later opnieuw. De link kan verlopen zijn.',
    fileNotFound: 'Het bestand is niet gevonden. De downloadlink is mogelijk verlopen. Vraag een nieuwe download aan.'
  },
  en: {
    default: 'An error occurred while downloading the files. Please try again later. The download link may have expired.',
    fileNotFound: 'The file could not be found. The download link may have expired. Please request a new download.'
  }
} as const

function getDownloadLanguage(): keyof typeof downloadErrorMessages {
  return document.body.classList.contains('lapide') ? 'en' : 'nl'
}

function getDownloadMessage(type: keyof (typeof downloadErrorMessages)['nl']) {
  const lang = getDownloadLanguage()
  return downloadErrorMessages[lang][type]
}

function updateLoadingModal(status: JobStatus) {
  if (status.worker) {
    loadingStatus.value = {
      status: status.worker.message ?? '',
      index: Number(status.worker.index)
    }
  } else {
    loadingStatus.value = {
      status: '',
      index: 0
    }
  }
}

export const jobStatusHandlerMap: JobStatusHandler = {
  delayed: (status, checkStatus) => {
    updateLoadingModal(status)
    setTimeout(checkStatus, pingTimeout)
  },
  'waiting-children': (status, checkStatus) => {
    updateLoadingModal(status)
    setTimeout(checkStatus, pingTimeout)
  },
  unknown: (status, checkStatus) => {
    updateLoadingModal(status)
    setTimeout(checkStatus, pingTimeout)
  },
  pending: (status, checkStatus) => {
    updateLoadingModal(status)
    setTimeout(checkStatus, pingTimeout)
  },
  waiting: (status, checkStatus) => {
    updateLoadingModal(status)
    setTimeout(checkStatus, pingTimeout)
  },
  active: (status, checkStatus) => {
    updateLoadingModal(status)
    setTimeout(checkStatus, pingTimeout)
  },
  processing: (status, checkStatus) => {
    updateLoadingModal(status)
    setTimeout(checkStatus, pingTimeout)
  },
  completed: (status, checkStatus) => {
    updateLoadingModal(status)
    if (status.worker) {
      downloadZipfile(status.worker)
    } else {
      setTimeout(checkStatus, pingTimeout)
    }
  },
  failed: (status) => {
    updateLoadingModal(status)
    setTimeout(closeLoadingModal, pingTimeout)
  }
}

async function parseDownloadError(response: Response): Promise<string> {
  try {
    const payload = (await response.json()) as DownloadErrorPayload
    const code = payload?.error?.code
    const message = payload?.error?.message

    if (code === 'FILE_NOT_FOUND') {
      return getDownloadMessage('fileNotFound')
    }

    if (message && typeof message === 'string') {
      return message
    }
  } catch {
    // No JSON payload available, fallback to a generic message.
  }

  return getDownloadMessage('default')
}

export async function downloadZipfile(worker: ExportWorker, mail: boolean = true) {
  let hasError = false

  try {
    const downloadUrl = `${import.meta.env.VITE_BACKEND_URL}download/${encodeURIComponent(worker.file)}`
    const response = await fetch(
      downloadUrl,
      mail
        ? {
            method: 'POST',
            headers: {
              'X-User': clientSession.value?.id ?? uuid.value
            },
            body: JSON.stringify({
              file: worker.file,
              mail,
              user: storedUserValues
            })
          }
        : {
            method: 'GET'
          }
    )

    if (!response.ok) {
      const errorMessage = await parseDownloadError(response)
      loadingStatus.value.status = errorMessage
      throw new Error(errorMessage)
    }

    const blob = await response.blob()
    const blobUrl = window.URL.createObjectURL(blob)
    const a = document.createElement('a')

    a.href = blobUrl
    a.download = worker.file
    document.body.appendChild(a)
    a.click()
    a.remove()
    window.URL.revokeObjectURL(blobUrl)
  } catch (error) {
    hasError = true
    loadingStatus.value.index = -1

    if (!loadingStatus.value.status) {
      loadingStatus.value.status = getDownloadMessage('default')
    }

    showToast({
      message: loadingStatus.value.status || getDownloadMessage('default'),
      type: 'error',
      duration: 5000
    })

    console.error('Error downloading file:', error)
    // throw error
  } finally {
    if (hasError) {
      if (mail) {
        setTimeout(() => {
          closeLoadingModal()
          loadingStatus.value.status = ''
          loadingStatus.value.index = 0
        }, pingTimeout)
      }
      return
    }

    loadingStatus.value.status = ''
    loadingStatus.value.index = 0

    window.gtag('event', 'download_texturen', {
      event_category: 'texturegenerator',
      event_label: `de texturen zijn gedownload`,
      value: 1
    })

    if (mail) {
      setTimeout(closeLoadingModal, 300)
    }
  }
}
